import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import config from 'constants/config'
import ContactContext from 'contexts/contactContext'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import useResponsiveSelector from 'hooks/useResponsiveSelector'
import LineIncomingCallIcon from 'LuxKit/Icons/line/LineIncomingCallIcon'

const SupportButtons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${rem(20)};

  > * + * {
    margin-inline-start: ${rem(8)};
  }
`

const HelpDescription = styled(BodyText)`
  margin-top: ${rem(8)};
`

interface Props {
  contactNumber?: App.GeoContact
  allowsAgentCallbackRequest?: boolean
}

function HelpSection({ contactNumber, allowsAgentCallbackRequest }: Props) {
  const intl = useIntl()
  const currentContact = useContext(ContactContext)
  const contact = contactNumber ?? currentContact.defaultContact

  const ctaText = useMemo(() => ({
    mobile: intl.formatMessage({
      id: 'WtuXPBHEtNMiXj_qpYUx',
      defaultMessage: 'Call now',
      description: 'Call now button text on mobile for checkout help section',
    }),
    tablet: intl.formatMessage({
      id: 'WtuXPBHEtNMiXj_qpYUx',
      defaultMessage: 'Call now',
      description: 'Call now button text on mobile for checkout help section',
    }),
  }), [intl])

  const ctaValue = useResponsiveSelector(ctaText) ?? contact.humanReadable

  const callbacksFeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.toursRequestAgentCallbackEnabled)

  const showModal = useContext(ModalContext)

  const handleCallbackClick = () => { showModal(<RequestAgentCallbackModal type="TOURS" />) }

  return <>
    <Heading variant="heading6">
      {intl.formatMessage({
        id: 'S4Cd9m9Q8MuwrdbQlqkg',
        defaultMessage: 'Need help with your booking?',
        description: 'Heading for checkout help section during the checkout and payment process',
      })}
    </Heading>
    <HelpDescription variant="medium">
      {intl.formatMessage(
        { id: 'hou5eueP9B25YInHSfU6', defaultMessage: 'Speak to a {brand} Expert anytime, 24 hours, 7 days a week', description: 'Description for checkout help section with brand name' },
        { brand: config.title },
      )}
    </HelpDescription>
    <SupportButtons>
      <TextButton kind="secondary" variant="dark" startIcon={<LinePhoneIcon />} href={`tel:${contact.number}`}>
        {ctaValue}
      </TextButton>
      {callbacksFeatureEnabled && allowsAgentCallbackRequest && <TextButton
        kind="secondary"
        variant="dark"
        onClick={handleCallbackClick}
        startIcon={<LineIncomingCallIcon />}
      >
        {intl.formatMessage({
          id: 'YvKui5PMUv_KzMMjOTON',
          defaultMessage: 'Request a callback',
          description: 'Button text to request a callback from a support agent',
        })}
      </TextButton>}
    </SupportButtons>
  </>
}

export default HelpSection
